import { useErrorMessages, useUserSession } from '@AppModels/hooks/appStateHooks';
import { IModalContext, ModalChildComponentContextKey } from '@AppModels/index';
import { useEventLogging } from '@AppModels/Logging';
import { useStartAutomationContactMutation } from '@Queries/Automation';
import { useAutomationContactPreviewQuery } from '@Queries/Automation/useAutomationContactPreviewQuery';
import { useAutomationTemplateIdQuery } from '@Queries/AutomationTemplate';
import { useContactAddTagsMutation } from '@Queries/Contact/useContactAddTagsMutation';
import { useGetSystemJobQuery } from '@Queries/SystemJob/useGetSystemJobQuery';
import { useTagSearchQuery } from '@Queries/Tag/useTagSearchQuery';
import { baseStyleSheet } from '@Styles/styles';
import * as Api from '@ViewModels';
import { AutomationStepsBy } from '@WebComponents/automation/AutomationSelector/AutomationStepsBy';
import { asModalComponent } from '@WebComponents/Modal';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import { canUserModifyTag, parseSkippedLockedTags } from '@AppModels/utils/tagUtils';
import {
	ISimpleAutoCompleteSearchFieldEvent,
	ISimpleAutoCompleteSearchFieldItemSelectionEvent,
	SimpleAutoCompleteSearchField,
} from '../../autocomplete/SimpleAutoCompleteSearchField';
import { useAddNewTagLogic } from '../../../hooks/useAddNewTagLogic';
import { AddNewTagFooter } from '../../tags/AddNewTagFooter';
import { Checkbox } from '../../Checkbox';
import { DeprecatedCloseButton } from '../../DeprecatedCloseButton';
import { LoadingSpinner } from '../../LoadingSpinner';
import { AutomationConfirmationHeader, AutomationStartedNotification } from './presentation';
import { styleSheet } from './styles';

interface Props extends IModalContext<string> {
	onAddTagButtonClicked?(tagValue: string, e: React.MouseEvent<HTMLElement>): void;
	request?: Api.IBulkContactsRequest;
}

enum TagModalStep {
	ShowingTagSearch,
	ShowingAutomationOption,
	ShowingAutomationConfirmation,
}

const getUniqueValuesBasedOnIds = (contacts: Partial<Api.IProjectedContact>[]) => {
	const uniqueIds = new Set<string>();
	const vals = (contacts || []).filter(x => {
		if (!uniqueIds.has(x.id)) {
			uniqueIds.add(x.id);
			return true;
		}
		return false;
	});
	return vals;
};

export function _ContactsAddTag({ request, onAddTagButtonClicked, parentModal }: Props) {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const { logInput, logApiError } = useEventLogging('ContactsAddTag');
	const [tagModalStep, setTagModalStep] = React.useState(TagModalStep.ShowingTagSearch);
	const [selectedTag, setSelectedTag] = React.useState<string | null>(null);
	const [selectedEmployee, setSelectedEmployee] = React.useState<Api.IUser | null>(null);
	const [selectedFrom, setSelectedFrom] = React.useState<Api.AutomationSelectionType>(
		Api.AutomationSelectionType.Myself
	);
	const [selectedAutomationTemplateId, setSelectedAutomationTemplateId] = React.useState<string | null>(null);
	const [sendToHousehold, setSendToHousehold] = React.useState(false);
	const isAdmin = userSession.account.isAdmin && userSession.account.features.automation.allowAdminToStartOnBehalf;
	const contactTagsMutation = useContactAddTagsMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			logApiError('ContactAddTagsMutation', error);
		},
	});
	const systemJobQuery = useGetSystemJobQuery({
		enabled: contactTagsMutation.isSuccess,
		jobId: contactTagsMutation.data?.id,
		refetchInterval: query => {
			const data = query.state.data;
			if (!data) {
				return false;
			}
			if (data.percentComplete < 100) {
				return 2000;
			}
			return false;
		},
	});
	const isSystemJobComplete =
		systemJobQuery.data?.percentComplete != null && systemJobQuery.data?.percentComplete >= 100;
	const trimmedSelectedTag = selectedTag?.trim();
	const tagSearchQuery = useTagSearchQuery({
		// Run during typing too so we can gate the Add button when the typed name
		// matches a locked tag for non-admins.
		enabled: !!trimmedSelectedTag || isSystemJobComplete,
		query: trimmedSelectedTag || '',
	});
	// Find an exact-name match in the search results to know if the user is about to
	// submit a locked tag. The autocomplete suggestions go through onTagSelected which
	// has its own check; this covers free-text typed values.
	const matchedSelectedTag = React.useMemo(() => {
		if (!tagSearchQuery.data || !trimmedSelectedTag) return null;
		return (
			tagSearchQuery.data.find(t => t.name?.toLocaleLowerCase() === trimmedSelectedTag.toLocaleLowerCase()) ?? null
		);
	}, [tagSearchQuery.data, trimmedSelectedTag]);
	const isSelectedTagLockedForUser =
		!!matchedSelectedTag?.isLocked && !canUserModifyTag(matchedSelectedTag, !!userSession.account.isAdmin);
	// While the search lookup is in flight for a non-empty value, we don't yet know if
	// the typed name resolves to a locked tag. Disable Add until it settles so a fast
	// click can't get ahead of the lock-state lookup. Includes background refetches
	// (TQ v5 isLoading goes false on cached re-opens while a refetch is in flight).
	const isLookupPendingForTypedTag =
		!userSession.account.isAdmin && !!trimmedSelectedTag && (tagSearchQuery.isPending || tagSearchQuery.isFetching);
	React.useEffect(() => {
		if (!isSystemJobComplete || !tagSearchQuery.data) {
			return;
		}
		const skipped = parseSkippedLockedTags(systemJobQuery.data?.additionalFields);
		if (skipped.length > 0) {
			toast.warning(`Skipped locked tags: ${skipped.join(', ')}.`);
		}
		const accountTag = tagSearchQuery.data.find(
			t => t.name?.toLocaleLowerCase() === selectedTag.toLocaleLowerCase() || (t.id && selectedTag === t.id)
		);
		if (!(accountTag?.automationTemplateId && userSession?.account?.features?.automation?.enabled)) {
			onConfirmationClose();
			return;
		}
		setSelectedAutomationTemplateId(accountTag.automationTemplateId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSystemJobComplete, tagSearchQuery.data, systemJobQuery.data, userSession]);
	const automationTemplateQuery = useAutomationTemplateIdQuery({
		id: selectedAutomationTemplateId,
		enabled: selectedAutomationTemplateId !== null,
	});
	React.useEffect(() => {
		if (!automationTemplateQuery.data) {
			return;
		}
		setSendToHousehold(
			automationTemplateQuery.data.sendToHousehold || userSession.account.features.households?.sendToHouseholdByDefault
		);
		setTagModalStep(TagModalStep.ShowingAutomationOption);
	}, [automationTemplateQuery.data, userSession.account.features.households?.sendToHouseholdByDefault]);
	const createAutomationRequest: Api.ICreateAutomationRequest = {
		...request,
		autoStart: true,
		putOnHold: false,
		groupByHousehold: sendToHousehold,
	};
	if (isAdmin) {
		createAutomationRequest.sendFromOptions = {
			mode:
				selectedFrom === Api.AutomationSelectionType.ContactOwners
					? Api.SendEmailFrom.ContactOwner
					: Api.SendEmailFrom.CurrentUser,
		};
		if (selectedFrom === Api.AutomationSelectionType.Myself) {
			createAutomationRequest.sendFromOptions.mode = Api.SendEmailFrom.SelectedUser;
			createAutomationRequest.sendFromOptions.selectedUser = userSession.user?.id;
		}
		if (selectedFrom === Api.AutomationSelectionType.Employee) {
			createAutomationRequest.sendFromOptions.mode = Api.SendEmailFrom.SelectedUser;
			createAutomationRequest.sendFromOptions.selectedUser = `${selectedEmployee?.id}`;
		}
	}
	const automationContactPreviewQuery = useAutomationContactPreviewQuery({
		id: selectedAutomationTemplateId,
		createAutomationRequest,
		enabled: selectedAutomationTemplateId != null,
	});
	const previewResult: Api.IPreviewCreateAutomationResult | undefined = automationContactPreviewQuery.data
		? {
				...automationContactPreviewQuery.data,
				excludeContacts: getUniqueValuesBasedOnIds(automationContactPreviewQuery.data.excludeContacts ?? []),
				contactOwners: getUniqueValuesBasedOnIds(automationContactPreviewQuery.data.contactOwners ?? []),
			}
		: undefined;
	const onCloseClicked = () => {
		if (parentModal) {
			parentModal.onRequestClose(null, true);
		}
	};
	const onSearchFieldChanged = (e: ISimpleAutoCompleteSearchFieldEvent<React.ChangeEvent<HTMLInputElement>>) => {
		if (e.sourceEvent) {
			const inputElement = e.sourceEvent.target as HTMLInputElement;
			setSelectedTag(inputElement.value || '');
		}
	};
	const onClearButtonClicked = () => {
		setSelectedTag(null);
	};
	const onTagSelected = (e: ISimpleAutoCompleteSearchFieldItemSelectionEvent<string | Api.IEntity | Api.IUser>) => {
		const selectionAsTag = typeof e.selection === 'object' ? (e.selection as Api.IAccountTag) : null;
		const tagName = typeof e.selection === 'string' ? e.selection : selectionAsTag?.name;
		// If the suggestion carries lock state, gate non-admin selection up front so the
		// 403 backstop only fires for free-text typed lockable names.
		if (selectionAsTag && !canUserModifyTag(selectionAsTag, !!userSession.account.isAdmin)) {
			toast.error('Only admins can apply this tag.');
			return;
		}
		if (e.target && tagName) {
			e.target.setSearchQuery(tagName);
		}
		setSelectedTag(tagName);
	};
	const onSearchFieldKeyDown = (e: ISimpleAutoCompleteSearchFieldEvent<React.KeyboardEvent<HTMLInputElement>>) => {
		if (e.sourceEvent && e.sourceEvent.keyCode === 13) {
			const inputElement = e.sourceEvent.target as HTMLInputElement;
			setSelectedTag(inputElement.value || '');
			inputElement.blur();
		}
	};
	const { handleAddNewTag, onTagSearchFieldRef } = useAddNewTagLogic({
		onTagSelected: tagName => {
			setSelectedTag(tagName);
		},
	});
	const onRenderResultsFooter = React.useCallback(
		(searchQuery: string, renderedItems: any[], totalCount: number) => {
			if (totalCount === 0 && searchQuery) {
				return <AddNewTagFooter searchQuery={searchQuery} onAddTag={handleAddNewTag} />;
			}
			return null;
		},
		[handleAddNewTag]
	);
	const onConfirmationClose = () => {
		parentModal?.onRequestClose(selectedTag, false);
	};
	const onSubmitClicked = (e: React.MouseEvent<HTMLElement>) => {
		const tag = selectedTag?.trim();
		if (!tag) {
			return;
		}
		// Defense in depth — the button is disabled in this case, but cover the race
		// where the search lookup resolves between click and handler.
		if (isSelectedTagLockedForUser) {
			toast.error('Only admins can apply this tag.');
			return;
		}
		if (onAddTagButtonClicked) {
			onAddTagButtonClicked(tag, e);
		}
		if (e.defaultPrevented || contactTagsMutation.isPending) {
			return;
		}
		logInput('Add', 'Click', { tagLength: tag.length });
		contactTagsMutation.mutate({
			request: {
				...request,
				tags: [tag],
			},
		});
	};
	const startAutomationContactMutation = useStartAutomationContactMutation({
		onError: error => {
			logApiError('StartAutomationBulkTag', error);
			errorMessages.pushApiError(error);
		},
		onSuccess: () => {
			setTagModalStep(TagModalStep.ShowingAutomationConfirmation);
		},
	});
	const onAutomationConfirmationRequest = (result?: Api.ICreateAutomationRequest, cancel?: boolean) => {
		if (!result && cancel) {
			setTagModalStep(TagModalStep.ShowingTagSearch);
			parentModal?.onRequestClose(selectedTag, false);
		} else {
			startAutomationContactMutation.mutate({
				id: selectedAutomationTemplateId,
				createAutomationRequest: result,
			});
		}
	};
	const onStartAutomationClicked = () => {
		if (!selectedEmployee && selectedFrom === Api.AutomationSelectionType.Employee) {
			return;
		}
		onAutomationConfirmationRequest(createAutomationRequest, false);
	};
	const onSendToHouseholdChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSendToHousehold(e.target.checked);
	};
	return (
		<div className={`${css(styleSheet.container)} contacts-add-tag`}>
			<div className={css(styleSheet.header)}>
				<DeprecatedCloseButton
					disabled={contactTagsMutation.isPending || (contactTagsMutation.isSuccess && !isSystemJobComplete)}
					onClick={onCloseClicked}
				/>
			</div>
			{tagModalStep !== TagModalStep.ShowingAutomationOption &&
				tagModalStep !== TagModalStep.ShowingAutomationConfirmation && (
					<div className={css(styleSheet.title)}>Add Tag</div>
				)}
			{contactTagsMutation.isSuccess && !isSystemJobComplete ? (
				<>
					<div className={css(styleSheet.taggingMessage)}>
						<div>Tagging contacts...</div>
						<LoadingSpinner type='small' />
					</div>
				</>
			) : (
				<>
					{tagModalStep === TagModalStep.ShowingTagSearch && (
						<div>
							<SimpleAutoCompleteSearchField
								onChange={onSearchFieldChanged}
								onClear={onClearButtonClicked}
								onItemSelected={onTagSelected}
								onKeyDown={onSearchFieldKeyDown}
								onRenderResultsFooter={onRenderResultsFooter}
								onSearchFieldRef={onTagSearchFieldRef}
								pageSize={10}
								placeholder='Search existing or enter new tag'
								renderNoResultsItem={false}
								resultsLimit={10}
								style={styleSheet.searchField}
								type={Api.ResourceAutoCompleteViewModelType.AccountTag}
							/>
							<button
								className={css(baseStyleSheet.ctaButton, styleSheet.submitButton)}
								onClick={onSubmitClicked}
								disabled={isSelectedTagLockedForUser || isLookupPendingForTypedTag}
								title={
									isSelectedTagLockedForUser
										? 'This tag is locked. Only admins can apply it.'
										: isLookupPendingForTypedTag
											? 'Checking lock status…'
											: undefined
								}
							>
								<span>Add</span>
							</button>
						</div>
					)}
					{tagModalStep === TagModalStep.ShowingAutomationOption && (
						<div className={`${css(styleSheet.automationConfirmContainer)} automation-confirmation`}>
							<AutomationConfirmationHeader automationTemplate={automationTemplateQuery.data} />
							{isAdmin ? (
								<article>
									<h4 className={css(baseStyleSheet.truncateText, styleSheet.automationCardSubTitle)}>
										Automation steps by:
									</h4>
									<AutomationStepsBy
										userSession={userSession}
										preview={previewResult}
										isBusy={automationContactPreviewQuery.isPending}
										onAutomationSelectionComesFrom={setSelectedFrom}
										onSelectedEmployee={setSelectedEmployee}
									/>
									{userSession.account.features.households?.enabled ? (
										<>
											<Checkbox
												className={css(styleSheet.sendToHoseholdCheckbox)}
												checked={sendToHousehold}
												type='large'
												onChange={onSendToHouseholdChanged}
											>
												<div>
													<p className={css(styleSheet.checkboxText)}>Send to households?</p>
												</div>
											</Checkbox>
											<p className={css(styleSheet.sendToHouseholdMessage)}>
												When this setting is turned on, if a contact in the automation is part of a household, the other
												household member will be included on any emails and handwritten cards sent as part of this
												automation.
											</p>
										</>
									) : null}
								</article>
							) : null}
							<footer className={css(styleSheet.buttonContainer)}>
								<button
									className={css(baseStyleSheet.ctaButton)}
									onClick={onStartAutomationClicked}
									disabled={!selectedEmployee && selectedFrom === Api.AutomationSelectionType.Employee}
								>
									<span>Start Automations for Tagged Contacts</span>
								</button>
								<button
									className={css(baseStyleSheet.ctaButtonReverse)}
									onClick={() => onAutomationConfirmationRequest(null, true)}
								>
									<span>Skip</span>
								</button>
							</footer>
						</div>
					)}
					{tagModalStep === TagModalStep.ShowingAutomationConfirmation && (
						<AutomationStartedNotification onAutomationConfirmClosed={onConfirmationClose} />
					)}
				</>
			)}
		</div>
	);
}

const ContactsAddTagAsObserver = observer(_ContactsAddTag);
export const ContactsAddTag = inject(ModalChildComponentContextKey)(ContactsAddTagAsObserver);

export const ContactsAddTagModal = asModalComponent(ContactsAddTag, {
	className: 'contacts-add-tag-modal',
	shouldCloseOnOverlayClick: false,
});
